import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// 🧩 Light and dark assets
import lumochartLogoLight from '../assets/lumochart-login.png';
import lumochartLogoDark from '../assets/lumochart-login-dark.png';
import lumodxLogoLight from '../assets/lumodx-logo.png';
import lumodxLogoDark from '../assets/lumodx-logo-dark.png';
import lumosyeLogoLight from '../assets/Lumosye-logo.png';
import lumosyeLogoDark from '../assets/Lumosye-logo-dark.png';

export default function ProductLayout({ children, accent = 'chart' }) {
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);

  // ✅ Live dark-mode detection
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() =>
      setIsDark(root.classList.contains('dark'))
    );
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    setIsDark(root.classList.contains('dark'));
    return () => observer.disconnect();
  }, []);

  // --- Dynamic product setup ---
  const product = {
    chart: {
      logo: isDark ? lumochartLogoDark : lumochartLogoLight,
      route: '/lumochart',
      hoverColor: 'hover:text-blue-600 dark:hover:text-blue-400',
    },
    dx: {
      logo: isDark ? lumodxLogoDark : lumodxLogoLight,
      route: '/lumodx',
      hoverColor: 'hover:text-lumodx-red dark:hover:text-lumodx-red',
    },
  }[accent];

  const lumosyeLogo = isDark ? lumosyeLogoDark : lumosyeLogoLight;

  // Scroll header behavior
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-black text-gray-800 dark:text-gray-200">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/80 dark:bg-zinc-900/80 backdrop-blur-lg shadow-md'
            : 'bg-transparent'
        }`}
      >
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to={product.route} className="flex items-center">
            <img
              src={product.logo}
              alt="Product Logo"
              className={`object-contain transition-transform duration-300 hover:scale-[1.03] ${
                accent === 'dx' ? 'h-14 md:h-16' : 'h-12 md:h-14'
              }`}
            />
          </Link>

          <div className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link
              to="/"
              className={`text-gray-600 dark:text-gray-300 ${product.hoverColor} transition`}
            >
              ← Back to Lumosye Health
            </Link>
            <Link
              to="/login"
              className={`text-gray-600 dark:text-gray-300 ${product.hoverColor} transition`}
            >
              Log In
            </Link>
          </div>
        </nav>
      </header>

      {/* Page Content */}
      <main className="flex-grow">{children}</main>

      {/* ✅ Updated Footer */}
      <footer className="bg-[#f3f5f7] dark:bg-zinc-950 text-gray-700 dark:text-gray-300 py-12 text-center border-t border-gray-300 dark:border-zinc-800 mt-16">
        <div className="container mx-auto flex flex-col items-center space-y-4">
          <img
            src={lumosyeLogo}
            alt="Lumosye Health Services Logo"
            className="h-10 md:h-12 object-contain drop-shadow-sm"
          />
          <p className="font-medium">
            © {new Date().getFullYear()} Lumosye Health Services, LLC
          </p>
          <div className="space-x-8 text-sm">
            <Link
              to="/privacy"
              className="hover:underline hover:text-blue-600 dark:hover:text-blue-400 transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:underline hover:text-blue-600 dark:hover:text-blue-400 transition"
            >
              Terms of Use
            </Link>
            <Link
              to="/support"
              className="hover:underline hover:text-blue-600 dark:hover:text-blue-400 transition"
            >
              Support
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
